"use client";

import { type BatchAdStatus, useBatchStatus } from "./batchStatus";

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2 2 21h20L12 2Z" />
      <path d="M12 9v5M12 18h.01" />
    </svg>
  );
}

function ChevronIcon({
  className,
  up = false,
}: {
  className?: string;
  up?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`${className} transition-transform duration-300`}
      style={{ transform: up ? "rotate(180deg)" : "rotate(0deg)" }}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-[1.5px] border-newform-purple border-t-transparent ${className ?? ""}`}
    />
  );
}

function AdStatusVisual({
  ad,
  batchIsProcessing,
}: {
  ad: BatchAdStatus;
  batchIsProcessing: boolean;
}) {
  if (ad.status === "FAILED_TO_CREATE") {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-destructive/12 text-destructive">
        <XIcon className="h-2.5 w-2.5" />
      </span>
    );
  }
  if (ad.metaAdId || ad.tiktokAdId || ad.googleAdId) {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600">
        <CheckIcon className="h-2.5 w-2.5" />
      </span>
    );
  }
  if (batchIsProcessing) {
    return <Spinner className="h-3 w-3" />;
  }
  return (
    <span className="block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
  );
}

function AdStatusLabel({
  ad,
  batchIsProcessing,
}: {
  ad: BatchAdStatus;
  batchIsProcessing: boolean;
}) {
  if (ad.status === "FAILED_TO_CREATE") return "Failed";
  if (ad.metaAdId || ad.tiktokAdId || ad.googleAdId) return "Live";
  if (batchIsProcessing) return "In queue";
  return "Pending";
}

function AdStatusRow({
  ad,
  batchIsProcessing,
}: {
  ad: BatchAdStatus;
  batchIsProcessing: boolean;
}) {
  const isLive = !!ad.metaAdId || !!ad.tiktokAdId || !!ad.googleAdId;
  const isFailed = ad.status === "FAILED_TO_CREATE";
  const isWaiting = batchIsProcessing && !isLive && !isFailed;

  return (
    <div className="flex items-center gap-2.5 px-3 py-2 text-xs">
      <div className="shrink-0">
        <AdStatusVisual ad={ad} batchIsProcessing={batchIsProcessing} />
      </div>
      <span className="flex-1 truncate font-medium text-foreground/85">
        {ad.fileName}
      </span>
      {isFailed && ad.errorUserMessage ? (
        <span
          className="max-w-[120px] shrink-0 truncate text-destructive"
          title={ad.errorUserMessage}
        >
          {ad.errorUserMessage}
        </span>
      ) : (
        <span
          className={`shrink-0 text-[10px] font-semibold uppercase tracking-wider ${
            isLive
              ? "text-emerald-600"
              : isFailed
                ? "text-destructive"
                : isWaiting
                  ? "text-newform-purple"
                  : "text-muted-foreground/70"
          }`}
        >
          <AdStatusLabel ad={ad} batchIsProcessing={batchIsProcessing} />
        </span>
      )}
    </div>
  );
}

export function FloatingStatusPanel() {
  const {
    activeBatches,
    batchData,
    isExpanded,
    toggleExpanded,
    dismissBatch,
    hasBatches,
  } = useBatchStatus();

  if (!hasBatches) return null;

  // Aggregate counts
  let totalAds = 0;
  let totalSuccessful = 0;
  let totalFailed = 0;

  for (const batch of activeBatches) {
    const data = batchData[batch.batchId];
    if (data) {
      totalAds += data.totalAds;
      totalSuccessful += data.successfulAds;
      totalFailed += data.failedAds;
    }
  }

  const totalDone = totalSuccessful + totalFailed;
  const progressPercent =
    totalAds > 0 ? Math.round((totalDone / totalAds) * 100) : 0;
  const allDone = totalAds > 0 && totalDone >= totalAds;
  const hasFailures = totalFailed > 0;

  const headerLabel =
    activeBatches.length === 1
      ? (batchData[activeBatches[0].batchId]?.batchName ?? "Launching batch")
      : `${activeBatches.length} batches`;

  const statusMessage = allDone
    ? hasFailures
      ? `${totalFailed} ad${totalFailed !== 1 ? "s" : ""} failed to upload`
      : "Batch launched successfully"
    : "Uploading ads to Meta…";

  return (
    <div
      className="fixed bottom-5 right-14 z-[9999] flex max-h-[38vh] w-[340px] flex-col overflow-hidden rounded-2xl border border-border/60 bg-background shadow-[0_18px_50px_-12px_rgba(15,23,42,0.22),0_6px_18px_-4px_rgba(15,23,42,0.08)]"
      style={{ animation: "cf-fsp-in 320ms cubic-bezier(0.22, 1, 0.36, 1)" }}
    >
      <style>{`
        @keyframes cf-fsp-in {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Header */}
      <button
        type="button"
        onClick={toggleExpanded}
        className="group flex w-full cursor-pointer items-center gap-3 px-4 pt-3.5 pb-3 text-left transition-colors hover:bg-muted/30"
      >
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
            !allDone
              ? "bg-newform-purple/10"
              : hasFailures
                ? "bg-destructive/10"
                : "bg-emerald-500/12"
          }`}
        >
          {!allDone && !isExpanded && <Spinner className="h-3.5 w-3.5" />}
          {!allDone && isExpanded && (
            <span className="h-1.5 w-1.5 rounded-full bg-newform-purple" />
          )}
          {allDone && !hasFailures && (
            <CheckIcon className="h-3.5 w-3.5 text-emerald-600" />
          )}
          {allDone && hasFailures && (
            <AlertIcon className="h-3.5 w-3.5 text-destructive" />
          )}
        </span>

        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-bold text-foreground">
            {headerLabel}
          </span>
          <span className="truncate text-[11px] text-muted-foreground">
            {statusMessage}
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {totalAds > 0 && (
            <span className="rounded-full bg-muted/60 px-2 py-0.5 text-[10px] font-bold tabular-nums text-foreground/70">
              {totalDone}/{totalAds}
            </span>
          )}
          <ChevronIcon
            up={isExpanded}
            className="h-4 w-4 text-muted-foreground/70 group-hover:text-foreground/80"
          />
        </div>
      </button>

      {/* Progress bar */}
      <div className="mx-4 mb-3 h-1 overflow-hidden rounded-full bg-muted/60">
        <div
          className={`h-full rounded-full transition-[width,background-color] duration-500 ease-out ${
            hasFailures
              ? "bg-destructive"
              : allDone
                ? "bg-emerald-500"
                : "bg-newform-purple"
          }`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Expanded content — CSS grid trick animates `grid-template-rows`
          between 0fr and 1fr, which smoothly resizes from 0 → content
          height. Inner `min-h-0 overflow-hidden` clips during the
          transition. Opacity fade runs in parallel so the content dissolves
          in/out rather than popping. */}
      <div
        className={`grid min-h-0 transition-[grid-template-rows,opacity] duration-300 ease-out ${
          isExpanded ? "flex-1" : ""
        }`}
        style={{
          gridTemplateRows: isExpanded ? "1fr" : "0fr",
          opacity: isExpanded ? 1 : 0,
        }}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="h-full overflow-y-auto border-t border-border/60 bg-muted/20">
            {activeBatches.map((batch) => {
              const data = batchData[batch.batchId];
              if (!data) {
                return (
                  <div
                    key={batch.batchId}
                    className="flex items-center gap-2.5 px-4 py-3 text-xs text-muted-foreground"
                  >
                    <Spinner className="h-3 w-3" />
                    Loading {batch.batchName}…
                  </div>
                );
              }

              const isTerminal =
                data.status === "ACTIVE" ||
                data.status === "FAILED" ||
                data.status === "PARTIALLY_FAILED";

              return (
                <div
                  key={batch.batchId}
                  className="border-b border-border/50 last:border-b-0"
                >
                  {activeBatches.length > 1 && (
                    <div className="flex items-center justify-between bg-background px-3 py-2">
                      <span className="truncate text-xs font-bold text-foreground">
                        {data.batchName}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold tabular-nums text-muted-foreground">
                          {data.successfulAds}/{data.totalAds}
                        </span>
                        {isTerminal && (
                          <button
                            type="button"
                            onClick={() => dismissBatch(batch.batchId)}
                            className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                            title="Dismiss"
                          >
                            <XIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Per-batch status message */}
                  {isTerminal && (
                    <div
                      className={`px-3 py-2 text-[11px] font-semibold ${
                        data.status === "ACTIVE"
                          ? "text-emerald-600"
                          : "text-destructive"
                      }`}
                    >
                      {data.status === "ACTIVE"
                        ? "Batch launched successfully"
                        : data.status === "PARTIALLY_FAILED"
                          ? `${data.failedAds} ad${data.failedAds !== 1 ? "s" : ""} failed`
                          : "Batch failed"}
                    </div>
                  )}

                  <div className="divide-y divide-border/40 bg-background">
                    {data.ads.map((ad) => (
                      <AdStatusRow
                        key={ad.id}
                        ad={ad}
                        batchIsProcessing={!isTerminal}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Dismiss for single completed batch */}
      {isExpanded && activeBatches.length === 1 && allDone && (
        <div className="border-t border-border/60 bg-background px-4 py-2.5">
          <button
            type="button"
            onClick={() => dismissBatch(activeBatches[0].batchId)}
            className="w-full rounded-full py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
